<template>
    <div class="dw-10 f fh fj-sb ath-table-super-header">
        <div v-for="header in uiHeaders"
            class="ath-super-header-cell sp-l-s"
            :class="[
                header.locked ? 'ath-lock-column' : '',
                header.left_locked ? 'ath-left-lock-column' : '',
                header.right_locked ? 'ath-right-lock-column' : '',
                header.add_divider ? 'right-divider' : '',
                header.text_classes,
            ]"
            :style="{
                'width': (UIMobile ? header.width_rem_mobile : header.width_rem) + 'rem',
                'left': (UIMobile ? header.left_position_mobile : header.left_position) + 'rem',
                'right': (UIMobile ? header.right_position_mobile : header.right_position) + 'rem',
            }"
            :key="header.uid">
            <div class="ath-super-header-cell-item f">
                <strong>
                    <span>{{ header.title | athT }}</span>
                </strong>
            </div>
        </div>
    </div>
</template>

<script>
import { guuid } from '@aretheyhappy/utils';
import UIMobileMixin from '../../../mixins/UIMobile';

/**
 * Format the super header
 */
function formatHeader (header, list, locked_dir) {
    let position = 0;
    let positionMobile = 0;

    for (const el of list) {
        position += el.width_rem;
        positionMobile += el.width_rem_mobile;
    }

    // Add the base header into the list
    list.push(Object.assign({}, header, {
        width_rem: header.width_rem / header.child_count,
        width_rem_mobile: header.width_rem_mobile / header.child_count,
        add_divider: header.child_count === 1,
        left_locked: header.locked && locked_dir === 'left',
        right_locked: header.locked && locked_dir === 'right',
    }, locked_dir === 'left' ? {
        left_position: position,
        left_position_mobile: positionMobile,
    } : {
        right_position: position,
        right_position_mobile: positionMobile,
    }));

    // For every child minus 1 (because the header itself counts as 1)
    // Add a fake child to help with spacing when table is wider than normal.
    // This fixes an issue where fj-sb adds extra width to all columns equally.
    // So in cases where we have 2 super headers and 10 child headers, we need to make sure
    // that the super headers with more children get more share of the width.
    // For example if one header has 2 children and the other has 8 children, normally
    // they would split the extra width 50/50. This change allows then to split it correctly.
    for (var i = 0; i < header.child_count - 1; i++) {
        list.push(Object.assign({
            uid: guuid(),
            title: '',
            width_rem: header.width_rem / header.child_count,
            width_rem_mobile: header.width_rem_mobile / header.child_count,
            add_divider: i === header.child_count - 2,
            locked: header.locked,
        }, locked_dir === 'left' ? {
            left_position: position / header.child_count,
            left_position_mobile: positionMobile / header.child_count,
        } : {
            right_position: position / header.child_count,
            right_position_mobile: positionMobile / header.child_count,
        }))
    }
}

export default {
    name: 'mead-table-super-header',
    mixins: [UIMobileMixin],
    props: {
        /**
         * The super-header definitions to render, each spanning one or more underlying columns
         * (via child_count) and contributing width/lock/position info used to lay them out above
         * the regular column headers.
         */
        superHeaders: {type: Array, default: () => []},
    },
    computed: {
        /**
         * Returns true if we have super headers defined
         */
        hasSuperHeaders: function () {
            return this.superHeaders.length > 0;
        },
        /**
         * Number of locked headers from the left side of the row
         */
        nrOfLockedLeftHeaders: function () {
            let nr = 0;

            for (const header of this.superHeaders) {
                if (!header.locked) break;

                nr++;
            }

            return nr;
        },
        /**
         * Number of locked headers from the right side of the row
         */
        nrOfLockedRightHeaders: function () {
            let nr = 0;

            const columns = [...this.superHeaders].reverse();
            for (const header of columns) {
                if (!header.locked) break;

                nr++;
            }


            return nr;
        },
        /**
         * Headers that are locked in the beginning of the row
         */
        leftLockedHeaders: function () {
            const list = [];

            if (this.nrOfLockedLeftHeaders === 0) return list;

            for (let i = 0; i < this.nrOfLockedLeftHeaders; i++) {
                formatHeader(this.superHeaders[i], list, 'left');
            }

            return list;
        },
        /**
         * Headers that are locked at the end of the row
         */
        rightLockedHeaders: function () {
            const list = [];

            if (this.nrOfLockedRightHeaders === 0) return list;

            for (let i = this.superHeaders.length - 1; i >= this.superHeaders.length - this.nrOfLockedRightHeaders; i--) {
                formatHeader(this.superHeaders[i], list, 'right');
            }

            return list.reverse();
        },
        /**
         * Build a new set of headers that includes fake ones used for spacing
         * in larger viewports.
         */
        computedHeaders: function () {
            const new_headers = [];
            for (let i = this.nrOfLockedLeftHeaders; i < this.superHeaders.length - this.nrOfLockedRightHeaders; i++) {
                formatHeader(this.superHeaders[i], new_headers, 'right');
            }
            return new_headers;
        },
        uiHeaders: function () {
            return [...this.leftLockedHeaders, ...this.computedHeaders, ...this.rightLockedHeaders];
        },
    },
};
</script>
<style lang="scss" scoped>
@import '../../../../sass/_variables.scss';
// Table header style
.ath-table-super-header {
    position: sticky;
    top: 0;
    z-index: 4;
    background-color: var(--c-panel-dark-bg);

    // Adds a border to the bottom without changing the height of the cell.
    // Fixes small 1px offset when scrolling down the list and the header being locked.
    box-shadow: inset 0 -1px 0 0 var(--c-panel-border);

    .ath-super-header-cell-item {
        min-height: 4rem;

        // Align title in the center of the row and overlap the cell to the right if too narrow
        span {
            position: absolute;
            top: 1.5rem;
        }
    }

    // Add a right divider between columns
    .right-divider:not(.ath-lock-column):not(:last-child) {
        box-shadow: inset -1px 0 0 0 var(--c-panel-border);
    }

    // Default sizing for cells. Needed so that smaller widths don't collapse the cells
    .ath-super-header-cell:not(.ath-lock-column) {
        min-width: 7rem;
    }

    // Ensure we have a good border bottom and border right when using sticky headers
    .ath-lock-column {
        box-shadow: inset -1px -1px 0 0 var(--c-panel-border);
    }
}
</style>